/**
 * #2099 — the translation between Configuration → Chat's screen shape and the
 * chat service's settings API.
 *
 * Pure, and separate from the component for the same reason `chatShapes.js` is
 * separate from `chatService.js`: the component cannot be loaded in a plain
 * node test (Vue, Vuex, the store), and this is where the mistakes live. Two
 * shapes, neither arbitrary:
 *
 *   the screen            the API (GET/PUT /api/clients/{uuid}/settings)
 *   ──────────            ───────────────────────────────────────────────
 *   chatOn  bool          enabled  bool
 *   groups  keyed by      kinds    a LIST of machine values
 *           DISPLAY NAME           ['all_members', 'job', ...]
 *   hours.days  an ARRAY  office_hours.days  an OBJECT keyed mon..sun
 *           Sunday-first
 *   hours.roles           outside_hours.roles
 *   employerCaReach       employer_ca_reach  bool, or NULL when the server
 *           bool | null            did not send it (#2121)
 *
 * ⚠ THE DAY ARRAY IS SUNDAY-FIRST AND MUST STAY THAT WAY. `chatPreview.js`
 * reads it as `hours.days[now.getDay()]`, and getDay() is 0 = Sunday — so a
 * Monday-first array (which is what the API's key order suggests) would shift
 * every day by one and the panel would close on the wrong evenings. The API's
 * side is an object, where order means nothing; the screen's side is an index.
 */

/**
 * Display name ↔ chat channel kind.
 *
 * The screen's keys are the words a Client Admin reads; the API's are
 * `ChannelKind` values. Kept as one ordered list so the two directions cannot
 * drift, and so the screen's row order is this file's decision rather than
 * whatever order the API happened to return `available_kinds` in.
 *
 * Retired groups (Client Admins, Skills, Status, Skills-by-Status — spec
 * decisions #25 and #30) are deliberately absent: the API refuses them and the
 * screen must not draw a switch that cannot be saved.
 */
export const GROUPS = [
    { label: 'Power Users', kind: 'power_users' },
    { label: 'Stewards', kind: 'stewards' },
    { label: 'All Members', kind: 'all_members' },
    { label: 'Job', kind: 'job' },
    { label: 'Employers / Payroll', kind: 'employer' },
];

/** Sunday-first, matching `Date#getDay()`. */
export const DAYS = [
    { key: 'sun', label: 'Sunday', short: 'Sun' },
    { key: 'mon', label: 'Monday', short: 'Mon' },
    { key: 'tue', label: 'Tuesday', short: 'Tue' },
    { key: 'wed', label: 'Wednesday', short: 'Wed' },
    { key: 'thu', label: 'Thursday', short: 'Thu' },
    { key: 'fri', label: 'Friday', short: 'Fri' },
    { key: 'sat', label: 'Saturday', short: 'Sat' },
];

/** The five roles the outside-hours switches cover (spec row 29). */
export const ROLE_KEYS = ['ca', 'pu', 'steward', 'member', 'employer'];

/**
 * #2106 — the closed message's cap and its fallback wording.
 *
 * `MAX_CLOSED_MESSAGE` is cs3-chat `OutsideHoursRules::MAX_MESSAGE`, and the
 * textarea's `maxlength` — so the server's 422 ("message is at most 500
 * characters.") is unreachable by typing rather than merely reported after the
 * save springs back.
 *
 * `DEFAULT_CLOSED_MESSAGE` is `PostRefusal::OutsideOfficeHours->message()`, the
 * sentence shown in place of a client message, and here it is the box's
 * PLACEHOLDER: the CA sees what their people will read if they write nothing.
 * The server sends it as `outside_hours.default_message`; this is the fallback
 * for a read that did not carry one.
 *
 * ⚠ SPELLED AGAIN, DELIBERATELY. `chatHours.js` has the same sentence for the
 * panel, and neither module may import the other: both are loaded under node by
 * copying the single file, and a relative import would not resolve (see the
 * `stage` helper in the tests). A test asserts the two are identical, which is
 * the guard a shared module would have given.
 */
export const MAX_CLOSED_MESSAGE = 500;
export const DEFAULT_CLOSED_MESSAGE = 'Chat is read-only outside your office hours.';

export function kindFor(label) {
    const hit = GROUPS.find((g) => g.label === label);

    return hit ? hit.kind : null;
}

export function labelFor(kind) {
    const hit = GROUPS.find((g) => g.kind === kind);

    return hit ? hit.label : null;
}

/**
 * The API's settings → the shape the screen already renders.
 *
 * ⚠ ONLY THE GROUPS THE API SAYS ARE AVAILABLE get a switch. `available_kinds`
 * is the server's list of what a CA may turn on, so a group the service has
 * retired stops being drawn without this file being edited — and a group it
 * adds appears, with a label, as soon as GROUPS knows one.
 */
export function toScreen(api) {
    const a = api || {};
    const on = Array.isArray(a.kinds) ? a.kinds : [];
    const available = Array.isArray(a.available_kinds) && a.available_kinds.length
        ? a.available_kinds
        : GROUPS.map((g) => g.kind);

    const groups = {};
    GROUPS.forEach((g) => {
        if (available.indexOf(g.kind) >= 0) {
            groups[g.label] = on.indexOf(g.kind) >= 0;
        }
    });

    const hours = (a.office_hours && typeof a.office_hours === 'object') ? a.office_hours : {};
    const stored = (hours.days && typeof hours.days === 'object') ? hours.days : {};
    const outside = (a.outside_hours && typeof a.outside_hours === 'object') ? a.outside_hours : {};
    const roles = (outside.roles && typeof outside.roles === 'object') ? outside.roles : {};

    const rolesOut = {};
    ROLE_KEYS.forEach((k) => { rolesOut[k] = roles[k] === true; });

    return {
        chatOn: a.enabled === true,
        groups,
        /*
         * #2121 — cs3-chat #37's per-client switch: may this client's employers
         * find and message its Client Admin (and the CA them).
         *
         * ⚠ NULL WHEN THE SERVER DID NOT SEND IT, and the screen hides the
         * switch on null. A chat server from before #37 has no such setting;
         * drawing it "off" there would describe a rule the server does not
         * have, and the first click would be a 422. Absent and non-boolean
         * both read as "not said" — the API only ever sends a boolean.
         */
        employerCaReach: typeof a.employer_ca_reach === 'boolean' ? a.employer_ca_reach : null,
        hours: {
            on: hours.enabled === true,
            tz: typeof hours.tz === 'string' && hours.tz !== '' ? hours.tz : 'UTC',
            // The API returns the timezone it stored and not where it came
            // from. The screen's "from the client record" note is a preview
            // affordance; in a live build there is nothing truthful to say, so
            // it says nothing rather than guessing.
            tzFromClient: null,
            days: DAYS.map((d) => {
                const one = stored[d.key] || {};

                return {
                    key: d.key,
                    label: d.label,
                    short: d.short,
                    open: one.open === true,
                    from: typeof one.from === 'string' ? one.from : '09:00',
                    to: typeof one.to === 'string' ? one.to : '17:00',
                };
            }),
            roles: rolesOut,
            /*
             * #2106 — the client's closed wording AS STORED, and the sentence
             * shown in its place.
             *
             * ⚠ AS STORED, NOT RESOLVED. This screen is the one place that must
             * tell "the CA wrote nothing" from "the CA wrote the stock
             * sentence": an empty box with the default in grey behind it is the
             * truth, and pre-filling the default would make the next save store
             * it as the client's own words. `ClientSettingsAdmin::present`
             * sends `message` unresolved for exactly this reason, with
             * `default_message` beside it (the chat BOX gets the resolved one).
             */
            closedMessage: typeof outside.message === 'string' ? outside.message : '',
            closedDefault: typeof outside.default_message === 'string' && outside.default_message !== ''
                ? outside.default_message
                : DEFAULT_CLOSED_MESSAGE,
            /*
             * ⚠ EMPTY IN A LIVE BUILD, AND THAT IS NOT AN OVERSIGHT.
             *
             * The screen names people by `employee_id` (spec row 21 says so
             * explicitly) and shows their name, role, phone and email. The chat
             * service stores `outside_hours.people` as `{ chat uid: bool }` —
             * a CS3 USER id, with nowhere to put a name, and no employee search
             * behind it. employee_id and user id are different identifiers, so
             * the list cannot round-trip without a resolver that does not exist
             * on either side yet.
             *
             * Rather than invent storage (which #2099 says not to do) or show a
             * list that silently fails to save, the per-person card is hidden in
             * a live build and listed as a gap. The ROLE switches above, which
             * the API does store, are wired.
             */
            people: [],
        },
    };
}

/**
 * The `kinds` list to PUT, from the screen's group switches.
 *
 * Order follows GROUPS rather than object iteration order, so two saves of the
 * same state produce the same request — which makes a diff of two payloads
 * mean something.
 *
 * ⚠ THIS IS A FULL REPLACE DERIVED FROM THE SWITCHES THE SCREEN DREW, so it is
 * coupled to `available_kinds`: a kind that is ON server-side but absent from
 * `available_kinds` has no switch, is therefore not in `groups`, and would be
 * turned OFF by the next group save. Not reachable today — `present()` filters
 * `kinds` and `available_kinds` through the same `isRetired()` — but it is the
 * thing to check if the service ever narrows `available_kinds` for a reason
 * other than retirement.
 */
export function kindsPatch(groups) {
    return {
        kinds: GROUPS
            .filter((g) => (groups || {})[g.label] === true)
            .map((g) => g.kind),
    };
}

export function enabledPatch(chatOn) {
    return { enabled: chatOn === true };
}

/**
 * #2121 — the "Employers can message the Client Admin" switch, and ONLY it.
 *
 * One key, because the API applies each key it is sent: a patch that carried
 * this beside a group or hours save would write this screen's copy over a
 * change made in another tab. And strictly boolean, like `kindsPatch`: cs3-chat
 * answers 422 ("employer_ca_reach must be true or false.") to anything else.
 */
export function employerCaReachPatch(on) {
    return { employer_ca_reach: on === true };
}

/**
 * The `office_hours` section to PUT.
 *
 * A whole section, because the API replaces a section whole — half a schedule
 * is not a schedule, which is why its input is sections and not keys.
 */
export function officeHoursPatch(hours) {
    const h = hours || {};
    const days = {};
    (h.days || []).forEach((d) => {
        days[d.key] = { open: d.open === true, from: d.from, to: d.to };
    });

    return {
        office_hours: {
            enabled: h.on === true,
            tz: h.tz,
            days,
        },
    };
}

/**
 * The `outside_hours` section to PUT.
 *
 * ⚠ `people` IS SENT AS IT WAS READ, NOT AS THE SCREEN HOLDS IT. The section
 * is replaced whole, so omitting `people` from a roles-only save would DELETE
 * every named person the service has stored. The screen cannot render that list
 * yet (see toScreen), so it carries the server's value through untouched rather
 * than sending `{}` and quietly wiping it.
 *
 * ⚠ #2106 — AND `message` IS THE ONE KEY THAT IS OMITTED UNLESS IT IS THE
 * THING BEING SAVED. It is the one key cs3-chat KEEPS when the section arrives
 * without it (`ClientSettingsInput`: absent = keep, `''` = clear), which is the
 * opposite of `people` above and for the same underlying reason — a save must
 * not destroy a field it was not about. A role toggle therefore sends no
 * message at all, so it cannot overwrite the CA's wording with whatever this
 * screen happened to load; the message's own save passes it and it is replaced.
 *
 * @param {object} roles     the screen's role switches
 * @param {object} knownPeople  `outside_hours.people` exactly as the API last returned it
 * @param {string} [message] the closed wording — OMIT to keep what is stored, '' to clear it
 */
export function outsideHoursPatch(roles, knownPeople, message) {
    const out = {};
    ROLE_KEYS.forEach((k) => { out[k] = (roles || {})[k] === true; });

    const section = {
        roles: out,
        people: (knownPeople && typeof knownPeople === 'object') ? knownPeople : {},
    };
    if (typeof message === 'string') section.message = message;

    return { outside_hours: section };
}

/**
 * The settings API's HTTP status → the code the screen explains.
 *
 * Pure, and here rather than in `chatService.js`, so it can be tested: the
 * service module pulls in axios and `./api` (Vue, Vuex, the store) and does not
 * load under node. The 422 branch in particular is load-bearing — #2099 asked
 * for the service's refusal shown plainly — so it should not be the one piece
 * with no coverage.
 */
export function settingsFailureCode(status) {
    return {
        401: 'chat_session_expired',
        // The service decides who may manage a client
        // (ChatPolicy::canManageClient) and answers one 403 for every reason.
        403: 'chat_not_permitted',
        // #21's contract gap, if it ever returns: a client id the route cannot
        // address does not match, so it 404s rather than 403s.
        404: 'chat_client_not_addressable',
        422: 'chat_settings_rejected',
    }[status] || 'chat_unreachable';
}

/**
 * The API's per-field 422, flattened into lines the screen can show.
 *
 * The field name is kept WITH the message rather than used to position it: this
 * screen is a page of switches with no per-control error slots, and the one
 * validated thing (a day's times) is a table row.
 *
 * @param {object} data  the response body, e.g.
 *   `{ error: 'invalid', errors: { 'office_hours.days.mon.to': ['Closing time must be after opening time.'] } }`
 */
export function fieldErrorsFrom(data) {
    const errors = data && data.errors;
    if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return [];

    const out = [];
    Object.keys(errors).forEach((field) => {
        const messages = Array.isArray(errors[field]) ? errors[field] : [errors[field]];
        messages.forEach((m) => {
            if (typeof m === 'string' && m !== '') out.push({ field, message: m });
        });
    });

    return out;
}

/**
 * Which roles may manage a client's chat settings, as the chat service decides
 * it (`ChatPolicy::canManageClient`): the client's own Client Admin, and CS
 * staff.
 *
 * Mirrored here only to decide whether to DRAW the screen's controls. The
 * service enforces it regardless and answers 403, which the screen reports —
 * this is not the gate, it is how the screen avoids offering a save that will
 * be refused.
 */
export function mayManage(chatRole) {
    return chatRole === 'ca' || chatRole === 'cs_admin' || chatRole === 'cs_support';
}

/**
 * #2117 — WHICH client's settings this screen is about, or null.
 *
 * Ed: *"when i login thru admin.stage- and view tristate > config > chat is
 * still a fail"*. Two different ids reach the panel and only one of them
 * answers this question:
 *
 *   session.cid               who is BILLED. Null for CS staff, by design
 *                             (#2111) — staff messages are billed to nobody.
 *   session.settingsClientId  whose SETTINGS may be opened. CS3 resolves it
 *                             from the working client and checks it
 *                             server-side (#2117); for a client user it is
 *                             the SAME value as `cid`.
 *
 * So the settings calls key off this, not off `cid`. `cid` is kept as the
 * fallback for one reason worth stating: a session cached by a build from
 * before #2117 has no `settingsClientId`, and for a Client Admin — every user
 * this screen had before — the two are equal, so the fallback is exact rather
 * than approximate.
 *
 * ⚠ IT IS NEVER TAKEN FROM THE URL OR FROM AN INPUT. The id arrives in CS3's
 * handoff response, having been proved there; the screen only reads it back.
 * cs3-chat still asks its own policy on every GET and PUT, so this decides
 * what is REQUESTED and never what is permitted.
 */
export function settingsClientId(session) {
    const s = session || {};

    return s.settingsClientId || s.cid || null;
}
